import { useCallback, useEffect, useMemo, useState } from 'react';
import { Calendar, Mail, MessageSquare, Phone, RefreshCw, User, UtensilsCrossed } from 'lucide-react';
import apiService from '../services/api';
import ConfirmDialog from './ConfirmDialog';
import Modal from './ui/Modal';

const STATUT_LABELS = {
  CONFIRME: { label: 'Confirmée', className: 'bg-green-100 text-green-800' },
  EN_ATTENTE: { label: 'À valider', className: 'bg-amber-100 text-amber-800' },
  EN_ATTENTE_PAIEMENT: { label: 'En attente paiement', className: 'bg-violet-100 text-violet-800' },
  ANNULE: { label: 'Refusée / annulée', className: 'bg-gray-100 text-gray-600' },
  TERMINE: { label: 'Terminée', className: 'bg-blue-100 text-blue-800' },
  NO_SHOW: { label: 'No-show', className: 'bg-red-100 text-red-800' },
};

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('fr-FR', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function formatDateTime(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('fr-FR', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatHeure(d) {
  if (!d) return '—';
  return new Date(d).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

function getClientName(r) {
  return (
    r.nomReservation ||
    [r.user?.prenom, r.user?.nom].filter(Boolean).join(' ') ||
    '—'
  );
}

function isGuestReservation(r) {
  return !r.userId && !r.user;
}

function formatEuroFr(amount) {
  return Number(amount).toLocaleString('fr-FR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function capturedArrhesAmount(r) {
  if (!r || r.arrhesExempt) return null;
  const paid = (r.paiements || []).filter(
    (p) => p.statutPaiement === 'VALIDE' && Number(p.montant) > 0
  );
  if (paid.length > 0) {
    return paid.reduce((sum, p) => sum + Number(p.montant), 0);
  }
  if (r.statut !== 'EN_ATTENTE_PAIEMENT' && Number(r.arrhesMontant) > 0) {
    return Number(r.arrhesMontant);
  }
  return null;
}

export default function ReservationsTablesManager({ commercantId, commercant }) {
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterStatut, setFilterStatut] = useState('all');
  const [from, setFrom] = useState(() => new Date().toISOString().slice(0, 10));
  const [refuseTarget, setRefuseTarget] = useState(null);
  const [refuseReason, setRefuseReason] = useState('');
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const load = useCallback(async () => {
    if (!commercantId) return;
    setLoading(true);
    setError('');
    try {
      const params = { from };
      if (filterStatut !== 'all') params.statut = filterStatut;
      const data = await apiService.getTableReservations(commercantId, params);
      const list = data.reservations || [];
      setReservations(list);
      setSelectedReservation((prev) => {
        if (!prev) return null;
        return list.find((item) => item.id === prev.id) || prev;
      });
      window.dispatchEvent(new CustomEvent('clickorico:refresh-table-reservations'));
    } catch (e) {
      setError(e?.message || 'Impossible de charger les réservations');
      setReservations([]);
    } finally {
      setLoading(false);
    }
  }, [commercantId, from, filterStatut]);

  useEffect(() => {
    load();
  }, [load]);

  const pendingCount = useMemo(
    () => reservations.filter((r) => r.statut === 'EN_ATTENTE').length,
    [reservations]
  );

  const updateStatut = async (id, statut, raison) => {
    setActionLoading(true);
    setError('');
    try {
      await apiService.updateTableReservationStatut(id, statut, raison);
      await load();
      return true;
    } catch (e) {
      setError(e?.message || 'Erreur lors de la mise à jour');
      return false;
    } finally {
      setActionLoading(false);
    }
  };

  const handleAccept = async (id) => {
    const ok = await updateStatut(id, 'CONFIRME');
    if (ok && selectedReservation?.id === id) {
      setSelectedReservation((prev) => (prev ? { ...prev, statut: 'CONFIRME' } : null));
    }
  };

  const handleMarkStatut = async (id, statut) => {
    const ok = await updateStatut(id, statut);
    if (ok && selectedReservation?.id === id) {
      setSelectedReservation((prev) => (prev ? { ...prev, statut } : null));
    }
    return ok;
  };

  const openRefuse = (r) => {
    setRefuseTarget(r);
    setRefuseReason('');
  };

  const handleRefuseConfirm = async () => {
    if (!refuseTarget) return false;
    const ok = await updateStatut(
      refuseTarget.id,
      'ANNULE',
      refuseReason.trim() || 'Refusée par le restaurant (complet ou indisponible)'
    );
    if (ok) {
      setRefuseTarget(null);
      setRefuseReason('');
      if (selectedReservation?.id === refuseTarget.id) {
        setSelectedReservation((prev) =>
          prev ? { ...prev, statut: 'ANNULE', raisonAnnulation: refuseReason.trim() || prev.raisonAnnulation } : null
        );
      }
    }
    return ok;
  };

  if (!commercant?.reservationTable) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-6 text-amber-900 text-sm">
        Activez « Réservation de table » dans l&apos;onglet Services pour recevoir des réservations en ligne.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <UtensilsCrossed className="h-5 w-5 text-rose-600" />
            Réservations de tables
            {pendingCount > 0 && (
              <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-800">
                {pendingCount} à valider
              </span>
            )}
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Acceptez ou refusez chaque demande. Le client peut suivre le statut dans son espace « Mes
            réservations ».
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          <RefreshCw className="h-4 w-4" />
          Actualiser
        </button>
      </div>

      <div className="flex flex-wrap gap-3 items-end">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">À partir du</label>
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Statut</label>
          <select
            value={filterStatut}
            onChange={(e) => setFilterStatut(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
          >
            <option value="all">Tous</option>
            <option value="EN_ATTENTE">À valider</option>
            <option value="CONFIRME">Confirmées</option>
            <option value="ANNULE">Refusées / annulées</option>
            <option value="TERMINE">Terminées</option>
            <option value="NO_SHOW">No-show</option>
          </select>
        </div>
      </div>

      {error && (
        <div className="p-3 rounded-lg bg-red-50 text-red-800 text-sm border border-red-200">{error}</div>
      )}

      {loading ? (
        <p className="text-sm text-gray-500">Chargement…</p>
      ) : reservations.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl border border-dashed border-gray-200">
          <Calendar className="h-10 w-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-600">Aucune réservation pour cette période</p>
        </div>
      ) : (
        <div className="overflow-x-auto border border-gray-200 rounded-xl">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Date</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Heure</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Client</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Pers.</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Tél.</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Statut</th>
                <th className="px-4 py-3 text-right font-medium text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {reservations.map((r) => {
                const st = STATUT_LABELS[r.statut] || STATUT_LABELS.EN_ATTENTE;
                const clientName = getClientName(r);
                const isGuest = isGuestReservation(r);
                const isPending = r.statut === 'EN_ATTENTE';
                return (
                  <tr
                    key={r.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelectedReservation(r)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setSelectedReservation(r);
                      }
                    }}
                    className={`cursor-pointer ${
                      isPending ? 'bg-amber-50/60 hover:bg-amber-50' : 'hover:bg-gray-50'
                    }`}
                  >
                    <td className="px-4 py-3 whitespace-nowrap">{formatDate(r.dateReservation)}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{formatHeure(r.heureArrivee)}</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1.5">
                        {clientName}
                        {isGuest && (
                          <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-slate-100 text-slate-600">
                            Invité
                          </span>
                        )}
                        {r.arrhesExempt && (
                          <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-emerald-100 text-emerald-800">
                            Sans arrhes
                          </span>
                        )}
                        {r.notes?.trim() && (
                          <MessageSquare
                            className="h-3.5 w-3.5 text-gray-400 shrink-0"
                            aria-label="Commentaire"
                          />
                        )}
                      </span>
                    </td>
                    <td className="px-4 py-3">{r.nombrePersonnes}</td>
                    <td className="px-4 py-3">{r.telephone || r.user?.telephone || '—'}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${st.className}`}>
                        {st.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right space-x-1 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      {isPending && (
                        <>
                          <button
                            type="button"
                            disabled={actionLoading}
                            onClick={() => handleAccept(r.id)}
                            className="inline-flex items-center rounded-md bg-green-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-green-700 disabled:opacity-50"
                          >
                            Accepter
                          </button>
                          <button
                            type="button"
                            disabled={actionLoading}
                            onClick={() => openRefuse(r)}
                            className="inline-flex items-center rounded-md bg-white border border-red-300 px-2.5 py-1 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50 ml-1"
                          >
                            Refuser
                          </button>
                        </>
                      )}
                      {r.statut === 'CONFIRME' && (
                        <>
                          <button
                            type="button"
                            disabled={actionLoading}
                            onClick={() => handleMarkStatut(r.id, 'TERMINE')}
                            className="text-blue-600 hover:underline text-xs font-medium"
                          >
                            Terminer
                          </button>
                          <button
                            type="button"
                            disabled={actionLoading}
                            onClick={() => handleMarkStatut(r.id, 'NO_SHOW')}
                            className="text-orange-700 hover:underline text-xs font-medium ml-2"
                          >
                            No-show
                          </button>
                          <button
                            type="button"
                            disabled={actionLoading}
                            onClick={() => openRefuse(r)}
                            className="text-red-600 hover:underline text-xs font-medium ml-2"
                          >
                            Annuler
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        isOpen={Boolean(selectedReservation)}
        onClose={() => setSelectedReservation(null)}
        title="Détail de la réservation"
        maxWidth="max-w-lg"
      >
        {selectedReservation && (() => {
          const r = selectedReservation;
          const st = STATUT_LABELS[r.statut] || STATUT_LABELS.EN_ATTENTE;
          const clientName = getClientName(r);
          const isGuest = isGuestReservation(r);
          const phone = r.telephone || r.user?.telephone;
          const email = r.email || r.user?.email;
          const isPending = r.statut === 'EN_ATTENTE';

          return (
            <div className="space-y-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-lg font-semibold text-gray-900">{clientName}</p>
                  <p className="text-sm text-gray-500 mt-0.5">
                    {formatDateTime(r.heureArrivee || r.dateReservation)}
                  </p>
                </div>
                <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium shrink-0 ${st.className}`}>
                  {st.label}
                </span>
              </div>

              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Personnes</dt>
                  <dd className="mt-1 font-medium text-gray-900">{r.nombrePersonnes}</dd>
                </div>
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Type client</dt>
                  <dd className="mt-1 font-medium text-gray-900">
                    {isGuest ? 'Invité (sans compte)' : 'Compte Clickorico'}
                  </dd>
                </div>
                {phone && (
                  <div className="sm:col-span-2">
                    <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide flex items-center gap-1">
                      <Phone className="h-3.5 w-3.5" />
                      Téléphone
                    </dt>
                    <dd className="mt-1">
                      <a href={`tel:${phone}`} className="font-medium text-primary-700 hover:underline">
                        {phone}
                      </a>
                    </dd>
                  </div>
                )}
                {email && (
                  <div className="sm:col-span-2">
                    <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide flex items-center gap-1">
                      <Mail className="h-3.5 w-3.5" />
                      Email
                    </dt>
                    <dd className="mt-1">
                      <a href={`mailto:${email}`} className="font-medium text-primary-700 hover:underline break-all">
                        {email}
                      </a>
                    </dd>
                  </div>
                )}
                {r.user && !isGuest && (
                  <div className="sm:col-span-2">
                    <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide flex items-center gap-1">
                      <User className="h-3.5 w-3.5" />
                      Compte
                    </dt>
                    <dd className="mt-1 text-gray-700">
                      {[r.user.prenom, r.user.nom].filter(Boolean).join(' ') || r.user.email}
                    </dd>
                  </div>
                )}
                {r.arrhesExempt && (
                  <div className="sm:col-span-2">
                    <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Arrhes</dt>
                    <dd className="mt-1 text-emerald-800 font-medium">Exemptée (client fidèle)</dd>
                  </div>
                )}
                {capturedArrhesAmount(r) != null && (
                  <div className="sm:col-span-2">
                    <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Arrhes</dt>
                    <dd className="mt-1 text-emerald-800 font-medium">
                      ✅ Arrhes encaissées : {formatEuroFr(capturedArrhesAmount(r))} €
                    </dd>
                    <p className="mt-1 text-xs text-gray-500">À déduire de l’addition du client.</p>
                  </div>
                )}
              </dl>

              <div>
                <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide flex items-center gap-1 mb-2">
                  <MessageSquare className="h-3.5 w-3.5" />
                  Commentaire client
                </h4>
                {r.notes?.trim() ? (
                  <p className="text-sm text-gray-800 bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 whitespace-pre-wrap">
                    {r.notes.trim()}
                  </p>
                ) : (
                  <p className="text-sm text-gray-400 italic">Aucun commentaire</p>
                )}
              </div>

              {r.raisonAnnulation && (
                <div>
                  <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                    Motif d&apos;annulation / refus
                  </h4>
                  <p className="text-sm text-red-800 bg-red-50 border border-red-200 rounded-xl px-4 py-3 whitespace-pre-wrap">
                    {r.raisonAnnulation}
                  </p>
                </div>
              )}

              {r.createdAt && (
                <p className="text-xs text-gray-400">
                  Demande reçue le {formatDateTime(r.createdAt)}
                </p>
              )}

              <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100" onClick={(e) => e.stopPropagation()}>
                {isPending && (
                  <>
                    <button
                      type="button"
                      disabled={actionLoading}
                      onClick={() => handleAccept(r.id)}
                      className="inline-flex items-center rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-50"
                    >
                      Accepter
                    </button>
                    <button
                      type="button"
                      disabled={actionLoading}
                      onClick={() => openRefuse(r)}
                      className="inline-flex items-center rounded-lg border border-red-300 px-4 py-2 text-sm font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50"
                    >
                      Refuser
                    </button>
                  </>
                )}
                {r.statut === 'CONFIRME' && (
                  <>
                    <button
                      type="button"
                      disabled={actionLoading}
                      onClick={() => handleMarkStatut(r.id, 'TERMINE')}
                      className="inline-flex items-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
                    >
                      Marquer terminée
                    </button>
                    <button
                      type="button"
                      disabled={actionLoading}
                      onClick={() => handleMarkStatut(r.id, 'NO_SHOW')}
                      className="inline-flex items-center rounded-lg border border-orange-300 px-4 py-2 text-sm font-semibold text-orange-800 hover:bg-orange-50 disabled:opacity-50"
                    >
                      Client absent (no-show)
                    </button>
                    <button
                      type="button"
                      disabled={actionLoading}
                      onClick={() => openRefuse(r)}
                      className="inline-flex items-center rounded-lg border border-red-300 px-4 py-2 text-sm font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50"
                    >
                      Annuler
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })()}
      </Modal>

      <ConfirmDialog
        isOpen={Boolean(refuseTarget)}
        title={refuseTarget?.statut === 'EN_ATTENTE' ? 'Refuser la demande' : 'Annuler la réservation'}
        description={
          refuseTarget?.statut === 'EN_ATTENTE'
            ? 'Le client verra la demande comme refusée. Indiquez une raison si vous le souhaitez (ex. complet, fermeture exceptionnelle).'
            : 'Cette réservation confirmée sera annulée.'
        }
        confirmLabel={refuseTarget?.statut === 'EN_ATTENTE' ? 'Refuser' : 'Annuler la réservation'}
        tone="danger"
        confirmDisabled={actionLoading}
        onCancel={() => {
          setRefuseTarget(null);
          setRefuseReason('');
        }}
        onConfirm={handleRefuseConfirm}
      >
        <label className="block text-sm font-medium text-gray-700">
          Motif (optionnel)
          <textarea
            value={refuseReason}
            onChange={(e) => setRefuseReason(e.target.value)}
            rows={3}
            className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            placeholder="Ex. Service complet à cette heure"
          />
        </label>
      </ConfirmDialog>
    </div>
  );
}
